// src/services/roster.js
import {
  collection, collectionGroup, doc, getDoc, getDocs, query, where,
  documentId, setDoc, writeBatch, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/** Build micro-profile để hiển thị nhanh ở subdocs */
export function pickDisplayFromUser(u = {}) {
  const name =
    u.displayName ||
    `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
    u.email ||
    "Unknown";
  return {
    name,
    email: u.email || "",
    avatarUrl: u.avatarUrl || u.photoURL || "",
  };
}

/** Fetch users theo batch (mỗi batch <=10 id) và trả map {id: data} */
export async function fetchUsersMap(ids = []) {
  if (!ids?.length) return {};
  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

  const snaps = await Promise.all(
    chunks.map((c) =>
      getDocs(query(collection(db, "users"), where(documentId(), "in", c)))
    )
  );
  const map = {};
  snaps.flatMap(s => s.docs).forEach(d => { map[d.id] = d.data(); });
  return map;
}

/** Ghi/ghi đè subdoc teachers từ list teacherIds (role mặc định: main) */
export async function upsertTeachers({ classId, teacherIds = [], role = "main" }) {
  if (!classId) throw new Error("Missing classId");
  const tMap = await fetchUsersMap(teacherIds);
  await Promise.all(
    teacherIds.map((tid) =>
      setDoc(doc(db, "classes", classId, "teachers", tid), {
        uid: tid,
        role,
        assignedAt: serverTimestamp(),
        display: pickDisplayFromUser(tMap[tid] || {}),
      }, { merge: true })
    )
  );
}

/** Ghi/ghi đè subdoc enrollments từ list user objects (có .id) */
export async function upsertEnrollmentsFromUsers({ classId, users = [], status = "active" }) {
  if (!classId) throw new Error("Missing classId");
  const ids = users.map(u => u.id);
  const uMap = await fetchUsersMap(ids);
  await Promise.all(
    users.map((u) =>
      setDoc(doc(db, "classes", classId, "enrollments", u.id), {
        uid: u.id,
        status,
        enrolledAt: serverTimestamp(),
        display: pickDisplayFromUser(uMap[u.id] || u),
      }, { merge: true })
    )
  );
}

/** Đồng bộ display cho 1 user tới tất cả classes(teachers|enrollments) */
export async function propagateDisplayForUser(uid, displayOverride = null) {
  if (!uid) return;
  const userSnap = await getDoc(doc(db, "users", uid));
  const display = displayOverride || pickDisplayFromUser(userSnap.exists() ? userSnap.data() : {});
  const [tSnap, eSnap] = await Promise.all([
    getDocs(query(collectionGroup(db, "teachers"), where("uid", "==", uid))),
    getDocs(query(collectionGroup(db, "enrollments"), where("uid", "==", uid))),
  ]);

  const refs = [...tSnap.docs.map(d => d.ref), ...eSnap.docs.map(d => d.ref)];
  if (!refs.length) return;

  const CHUNK = 450; // < 500/commit
  for (let i = 0; i < refs.length; i += CHUNK) {
    const batch = writeBatch(db);
    refs.slice(i, i + CHUNK).forEach(ref => batch.update(ref, { display }));
    await batch.commit();
  }
}

/** (Tuỳ chọn) Backfill display cho 1 lớp nếu dữ liệu cũ thiếu */
export async function backfillRosterDisplayForClass(classId) {
  const [tSnap, eSnap] = await Promise.all([
    getDocs(collection(db, "classes", classId, "teachers")),
    getDocs(collection(db, "classes", classId, "enrollments")),
  ]);
  const missing = [
    ...tSnap.docs.filter(d => !d.data()?.display).map(d => ({ ref: d.ref, uid: d.id })),
    ...eSnap.docs.filter(d => !d.data()?.display).map(d => ({ ref: d.ref, uid: d.id })),
  ];
  if (!missing.length) return;

  const ids = [...new Set(missing.map(m => m.uid))];
  const uMap = await fetchUsersMap(ids);

  const batch = writeBatch(db);
  missing.forEach(({ ref, uid }) => {
    batch.set(ref, { display: pickDisplayFromUser(uMap[uid] || {}) }, { merge: true });
  });
  await batch.commit();
}
